import { TagCategoryKey } from "../../models/freeform-tags.model";
import { SegmentConfigResponse } from "../../../admin/themes/models/theme.model";

export type FreeformSortColumn = "SCORE" | "PRIORITY" | "SUBMISSION_TIME";
export type SortDirection = "ASC" | "DESC";
export type FreeformSubmissionState = "ACTIVE" | "ARCHIVED";

export interface SubmissionFreeformSearchRequest {
    interactiveIds: string[];
    page?: number;
    size?: number;
    sortColumn?: FreeformSortColumn;
    sortDirection?: SortDirection;
    keyword?: string;
    isVoice?: boolean;
    isText?: boolean;
    isVideo?: boolean;
    isInappropriate?: boolean;
    isSpam?: boolean;
    minimumScore?: number;
    tags?: Partial<Record<TagCategoryKey, string[]>>;
    isFavorite?: boolean;
    isUsed?: boolean;
    isPublic?: boolean;
    hasResponse?: boolean;
    hasPhoneNumber?: boolean;
    profileId?: string;
    deviceId?: string;
    segmentId?: string;
    state?: FreeformSubmissionState;
}

export type SubmissionFreeformUpdateCleanedTextAndTagsRequest = Partial<Record<TagCategoryKey, null>> & {
    cleanedText?: string | null;
};

export interface SubmissionFreeformHostResponseRequest {
    responseRating?: string | null;
    responseCustomText?: string | null;
    responseAudioPath?: string | null;
    responseHideBadgeImage?: boolean;
    responseLink?: string | null;
}

export interface HostAudioUploadResponse {
    objectKey: string;
}

export interface SubmissionFreeformResponse {
    id: string;
    interactiveId: string;
    profileId: string | null; // long-lived UUID for Atlas user or null for anonymous/unauthenticated/hotline submissions
    submitterContactId: string | null; // UUID of submitter details, TTL of ~30 days.
    hasPhoneNumber: boolean;
    devicePlatform: string;
    deviceId: string | null;
    segmentId: string | null;
    segmentLabel: string | null;
    textSubmission: string | null;
    audioSubmissionPath: string | null;
    hasAudio: boolean;
    videoSubmissionPath: string | null;
    hasVideo: boolean;
    submissionTime: string;
    cleanedText: string | null;
    shortDescription: string | null;
    isInappropriate: boolean | null;
    isSpam: boolean | null;
    topic: string | null;
    score: number | null;
    audioEnergy: number | null; // 1–5 scale
    tagCategoryA: string | null;
    tagCategoryB: string | null;
    tagCategoryC: string | null;
    tagCategoryD: string | null;
    tagCategoryE: string | null;
    tagCategoryF: string | null;
    priority: number | null;
    processedAt: string | null;
    isFavorite: boolean;
    isUsed: boolean;
    isPublic: boolean;
    isFeatured: boolean;
    isHighlighted: boolean;
    notes: string | null;
    state: FreeformSubmissionState;
    attachmentPath: string | null;
    responseRating: string | null;
    responseCustomText: string | null;
    responseAudioPath: string | null;
    responseHideBadgeImage: boolean;
    responseLink: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface SubmissionFreeformSearchResponse {
    items: SubmissionFreeformResponse[];
    page: number;
    size: number;
    totalItems: number;
    totalPages: number;
}

export interface FreeformSegmentsResponse {
    segments: SegmentConfigResponse[];
}

export interface AudioFile {
    duration: number;
    size: number;
    url: string;
    error: string;
}

export const AUDIO_FILE_EXTENSIONS = {
    "audio/mpeg": "mp3",
    "audio/mp4": "m4a",
    "audio/x-m4a": "m4a",
    "audio/wav": "wav",
    "audio/webm": "webm",
    "audio/ogg": "ogg"
} as const;
export type SubmissionAudioMimeType = keyof typeof AUDIO_FILE_EXTENSIONS;
export function isSubmissionAudioMimeType(mimeType: string): mimeType is SubmissionAudioMimeType {
    return mimeType in AUDIO_FILE_EXTENSIONS;
}

export const VIDEO_FILE_EXTENSIONS = {
    "video/mp4": "mp4",
    "video/quicktime": "mov"
} as const;
export type SubmissionVideoMimeType = keyof typeof VIDEO_FILE_EXTENSIONS;
export function isSubmissionVideoMimeType(mimeType: string): mimeType is SubmissionVideoMimeType {
    return mimeType in VIDEO_FILE_EXTENSIONS;
}

export const MAX_AUDIO_SIZE_MB = 5;
export const MAX_AUDIO_SIZE_BYTES = MAX_AUDIO_SIZE_MB * 1024 * 1024;

export interface SubmissionFreeformBulkMoveRequest {
    submissionIds: string[];
    targetInteractiveId: string;
}

export type SubmissionFreeformBulkResultStatus = "SUCCESS" | "FAILURE";

export interface SubmissionFreeformBulkResult {
    id: string;
    status: SubmissionFreeformBulkResultStatus;
    submission: SubmissionFreeformResponse | null;
    error: string | null;
}

export interface SubmissionFreeformBulkUpdateResponse {
    results: SubmissionFreeformBulkResult[];
}
